import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Minus, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMessages } from '../../contexts/MessagesContext';
import { useContacts } from '../../contexts/ContactsContext';
import { useAuth } from '../../contexts/AuthContext';
import ChatWindow from './ChatWindow';
import Draggable from 'react-draggable';

export default function FloatingChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const { messages } = useMessages();
  const { contacts } = useContacts();
  const { currentUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const nodeRef = useRef(null);

  useEffect(() => {
    // Só contar mensagens não lidas destinadas ao utilizador autenticado
    const count = contacts.reduce((acc, contact) => {
      if (contact.contactId === currentUser?.uid) return acc; // nunca contar para o próprio
      if (contact.unreadCount && contact.unreadCount > 0 && contact.lastMessage?.senderId !== currentUser?.uid) {
        return acc + contact.unreadCount;
      }
      return acc;
    }, 0);
    setUnreadCount(count);
  }, [contacts, currentUser]);

  // O return condicional deve vir depois dos hooks
  if (!currentUser || currentUser.role === 'customer') return null;

  const minimizeChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  const openFullMessages = () => {
    navigate('/dashboard/messages');
    setIsOpen(false);
  };

  return (
    <Draggable bounds="body" nodeRef={nodeRef}>
      <div ref={nodeRef} className="fixed bottom-4 right-4 z-50" style={{ cursor: 'move' }}>
        {isOpen && (
          <div 
            className={`bg-white rounded-lg shadow-xl mb-4 transition-all duration-300 ease-in-out
              ${isMinimized ? 'h-14' : 'h-[600px]'}
              w-[800px] overflow-hidden`}
          >
            {/* Barra de título */}
            <div className="bg-[#C5D86D] text-white p-3 flex justify-between items-center">
              <h3 className="font-medium text-lg">Mensagens</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={openFullMessages}
                  className="px-3 py-1 hover:bg-black/10 rounded transition-colors text-sm font-medium"
                  title="Abrir página completa de mensagens"
                >
                  Abrir completo
                </button>
                <button
                  onClick={minimizeChat}
                  className="p-1 hover:bg-black/10 rounded transition-colors"
                  title={isMinimized ? "Maximizar" : "Minimizar"}
                >
                  {isMinimized ? <Maximize2 size={18} /> : <Minus size={18} />}
                </button>
                <button
                  onClick={toggleChat}
                  className="p-1 hover:bg-black/10 rounded transition-colors"
                  title="Fechar"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Conteúdo do chat */}
            <div className={`h-[calc(100%-56px)] ${isMinimized ? 'hidden' : ''}`}>
              <ChatWindow />
            </div>
          </div>
        )}

        {/* Botão flutuante quando fechado */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="bg-primary hover:bg-primary-dark text-white p-4 rounded-full shadow-lg relative"
          >
            <MessageCircle size={28} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        )}
      </div>
    </Draggable>
  );
}
